// src/services/srsService.js
//
// Phase 1 (PLAN1.md) — FSRS-4.5 Spaced Repetition Engine.
// ------------------------------------------------------------------------
// Each `reviewQueue` doc represents ONE memorised portion (rub 1..240)
// for ONE student. The scheduler uses `ts-fsrs` — a port of FSRS-4.5 — to
// compute the next due date from stability + difficulty rather than the
// crude SM-2 ease factor the previous stub used. This gives us two things
// SM-2 could not: retention-targeted intervals, and a real forgetting
// curve we can plot on the UI.
//
// Firestore document shape:
//   {
//     studentId, portionId,
//     state: 'new' | 'learning' | 'reviewing' | 'mastered',
//     fsrs: {
//       due: Timestamp,
//       stability: number,
//       difficulty: number,
//       elapsed_days: number,
//       scheduled_days: number,
//       reps: number,
//       lapses: number,
//       learning_steps: number,
//       state: 0..3,
//       last_review: Timestamp | null
//     },
//     dueDate: 'YYYY-MM-DD',      // denormalised for cheap range queries
//     interval: number,            // derived, for legacy UI compatibility
//     retrievability: number,      // 0..1, snapshot at last write
//     lastQuality: 1..5 | null,
//     lastReviewedAt: Timestamp | null,
//     createdAt, updatedAt
//   }
// ------------------------------------------------------------------------

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import {
  FSRS,
  Rating as FsrsRating,
  State as FsrsState,
  createEmptyCard,
  generatorParameters
} from 'ts-fsrs';
import { db } from '../firebase/config';
import { getCurrentTenantId } from './tenantContext';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CARD_STATES = {
  NEW: 'new',
  LEARNING: 'learning',
  REVIEWING: 'reviewing',
  MASTERED: 'mastered'
};

// Default desired retention — a teacher can override per-tenant later.
// 0.9 = "I want 90% probability the student still remembers when due".
const DEFAULT_REQUEST_RETENTION = 0.9;

// A portion is retired ("mastered") once stability crosses 180 days and
// the student has answered correctly at least 6 times in a row.
const MASTERY_STABILITY_DAYS = 180;
const MASTERY_MIN_REPS = 6;

// ---------------------------------------------------------------------------
// FSRS singleton
// ---------------------------------------------------------------------------

const fsrs = new FSRS(
  generatorParameters({ request_retention: DEFAULT_REQUEST_RETENTION })
);

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Format a Date as YYYY-MM-DD (UTC). */
export const toDateString = (d) => {
  if (typeof d === 'string') return d.length >= 10 ? d.slice(0, 10) : d;
  return d.toISOString().split('T')[0];
};

/** Add N days to a YYYY-MM-DD string. */
export const addDaysToString = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return toDateString(d);
};

/**
 * Map the app's 1..5 teacher quality rating onto an FSRS Rating
 * (Again / Hard / Good / Easy). We pick Good as the neutral anchor at 3
 * because the rest of the UI treats 3 as "acceptable".
 */
export const qualityToFsrsRating = (quality) => {
  if (typeof quality !== 'number' || Number.isNaN(quality)) return FsrsRating.Again;
  if (quality >= 4.5) return FsrsRating.Easy;
  if (quality >= 3.5) return FsrsRating.Good;
  if (quality >= 2.5) return FsrsRating.Hard;
  return FsrsRating.Again;
};

/**
 * Translate an FSRS numeric state (0..3) into the app-level state string
 * the UI expects (used for chip colour + mastery filtering).
 */
const fsrsStateToApp = (fsrsState, fsrsCard) => {
  if (!fsrsCard) return CARD_STATES.NEW;
  if (
    fsrsCard.stability >= MASTERY_STABILITY_DAYS &&
    fsrsCard.reps >= MASTERY_MIN_REPS &&
    fsrsCard.lapses <= 1
  ) {
    return CARD_STATES.MASTERED;
  }
  switch (fsrsState) {
    case FsrsState.Learning:
    case FsrsState.Relearning:
      return CARD_STATES.LEARNING;
    case FsrsState.Review:
      return CARD_STATES.REVIEWING;
    case FsrsState.New:
    default:
      return CARD_STATES.NEW;
  }
};

/** Convert an incoming Firestore/plain Date-ish field into a real Date. */
const asDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Hydrate a Firestore card doc into an FSRS-ready `Card` object. If the
 * doc has no `fsrs` block (first-time write, legacy SM-2 doc) we return
 * a fresh empty FSRS card so the scheduler can treat it as a brand-new
 * review.
 */
const hydrateFsrsCard = (docData) => {
  const f = docData?.fsrs;
  if (!f) {
    return createEmptyCard();
  }
  return {
    due: asDate(f.due) || new Date(),
    stability: Number(f.stability) || 0,
    difficulty: Number(f.difficulty) || 0,
    elapsed_days: Number(f.elapsed_days) || 0,
    scheduled_days: Number(f.scheduled_days) || 0,
    reps: Number(f.reps) || 0,
    lapses: Number(f.lapses) || 0,
    learning_steps: Number(f.learning_steps) || 0,
    state: typeof f.state === 'number' ? f.state : FsrsState.New,
    last_review: asDate(f.last_review)
  };
};

/** Serialise an FSRS card back into plain-JSON for Firestore. */
const serialiseFsrsCard = (card) => ({
  due: Timestamp.fromDate(card.due instanceof Date ? card.due : new Date(card.due)),
  stability: card.stability,
  difficulty: card.difficulty,
  elapsed_days: card.elapsed_days,
  scheduled_days: card.scheduled_days,
  reps: card.reps,
  lapses: card.lapses,
  learning_steps: card.learning_steps || 0,
  state: card.state,
  last_review: card.last_review
    ? Timestamp.fromDate(
        card.last_review instanceof Date
          ? card.last_review
          : new Date(card.last_review)
      )
    : null
});

// ---------------------------------------------------------------------------
// Pure scheduler — unit-testable, no Firestore dependency
// ---------------------------------------------------------------------------

/**
 * Given the current card doc and a fresh 1..5 quality rating, run FSRS
 * and return the next persisted fields. The returned shape matches the
 * Firestore schema so callers can spread it into an upsert.
 */
export const scheduleNext = (card, quality, now = new Date()) => {
  const fsrsRating = qualityToFsrsRating(quality);
  const fsrsCard = hydrateFsrsCard(card);
  const { card: nextFsrs } = fsrs.next(fsrsCard, now, fsrsRating);

  const appState = fsrsStateToApp(nextFsrs.state, nextFsrs);
  const due = nextFsrs.due instanceof Date ? nextFsrs.due : new Date(nextFsrs.due);
  const dueDate = toDateString(due);
  const interval = Math.max(0, Math.round(nextFsrs.scheduled_days || 0));
  // Retrievability *at the due date* — closest to 0.9 by design; we also
  // snapshot current retrievability so the UI can sort by "most urgent".
  const retrievability = clampRetrievability(
    fsrs.get_retrievability(nextFsrs, now, false)
  );

  return {
    state: appState,
    fsrs: serialiseFsrsCard(nextFsrs),
    dueDate,
    interval,
    retrievability,
    lastQuality: typeof quality === 'number' ? quality : null,
    lastReviewedAt: Timestamp.fromDate(now)
  };
};

const clampRetrievability = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return Math.round(n * 10000) / 10000;
};

/** Deterministic document id so each (student, portion) pair is unique. */
export const buildCardId = (studentId, portionId) =>
  `${studentId}_${portionId}`;

/** Fresh FSRS-backed initial card. */
export const buildInitialCard = (studentId, portionId, now = new Date(), tenantId = null) => {
  const empty = createEmptyCard(now);
  const card = {
    studentId,
    portionId,
    state: CARD_STATES.NEW,
    fsrs: serialiseFsrsCard(empty),
    dueDate: toDateString(now),
    interval: 0,
    retrievability: null,
    lastQuality: null,
    lastReviewedAt: null,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now)
  };
  if (tenantId) card.tenantId = tenantId;
  return card;
};

// ---------------------------------------------------------------------------
// Retrievability helpers — used by UI (forgetting curve, priority sort)
// ---------------------------------------------------------------------------

/**
 * Compute current retrievability for a stored card. Returns a number in
 * [0, 1] or null if the card has never been reviewed.
 */
export const currentRetrievability = (cardDoc, now = new Date()) => {
  const f = cardDoc?.fsrs;
  if (!f || !f.last_review) return null;
  const fsrsCard = hydrateFsrsCard(cardDoc);
  return clampRetrievability(fsrs.get_retrievability(fsrsCard, now, false));
};

/**
 * Build points for a forgetting curve: days-ahead → predicted retention.
 * Returns `[{ day, retention }]` suitable for Recharts.
 */
export const forgettingCurvePoints = (cardDoc, { horizonDays = 60, stepDays = 2 } = {}) => {
  const f = cardDoc?.fsrs;
  if (!f) return [];
  const fsrsCard = hydrateFsrsCard(cardDoc);
  if (!fsrsCard.last_review || !fsrsCard.stability) return [];
  const points = [];
  const base =
    fsrsCard.last_review instanceof Date
      ? fsrsCard.last_review
      : new Date(fsrsCard.last_review);
  for (let day = 0; day <= horizonDays; day += stepDays) {
    const at = new Date(base.getTime() + day * 86400000);
    const r = clampRetrievability(fsrs.get_retrievability(fsrsCard, at, false));
    if (r == null) continue;
    points.push({ day, retention: r });
  }
  return points;
};

/**
 * Priority score for the "due today" queue — smaller retention = more
 * urgent. Falls back to dueDate for cards never reviewed.
 */
export const cardPriority = (cardDoc, now = new Date()) => {
  const r = currentRetrievability(cardDoc, now);
  if (r != null) return r;
  // Never reviewed — treat as very urgent.
  if (!cardDoc?.dueDate) return 0;
  return 0.0001;
};

// ---------------------------------------------------------------------------
// Firestore service
// ---------------------------------------------------------------------------

export const srsService = {
  async getCard(studentId, portionId) {
    const ref = doc(db, 'reviewQueue', buildCardId(studentId, portionId));
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async upsertCard(studentId, portionId, patch = {}) {
    const id = buildCardId(studentId, portionId);
    const ref = doc(db, 'reviewQueue', id);
    // Firestore rules require every reviewQueue write to include a
    // tenantId matching the caller's tenant (incomingSameTenant()).
    // We inject it here so every call path — recordReview, snooze,
    // advance, retire, quiz feedback — ends up with the field set.
    const tid = getCurrentTenantId();
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const updates = { ...patch, updatedAt: Timestamp.now() };
      if (tid) updates.tenantId = tid;
      await updateDoc(ref, updates);
    } else {
      await setDoc(ref, {
        ...buildInitialCard(studentId, portionId, new Date(), tid),
        ...patch
      });
    }
    return id;
  },

  async initializeCardsForStudent(studentId, portionIds, now = new Date()) {
    if (!Array.isArray(portionIds) || portionIds.length === 0) return [];
    const tid = getCurrentTenantId();
    const created = [];
    const batch = writeBatch(db);
    for (const portionId of portionIds) {
      const id = buildCardId(studentId, portionId);
      const ref = doc(db, 'reviewQueue', id);
      // eslint-disable-next-line no-await-in-loop
      const existing = await getDoc(ref);
      if (!existing.exists()) {
        batch.set(ref, buildInitialCard(studentId, portionId, now, tid));
        created.push(id);
      }
    }
    if (created.length > 0) {
      await batch.commit();
    }
    return created;
  },

  async recordReview(studentId, portionId, quality, now = new Date()) {
    const current = await srsService.getCard(studentId, portionId);
    const next = scheduleNext(current || {}, quality, now);
    await srsService.upsertCard(studentId, portionId, next);
    return { id: buildCardId(studentId, portionId), ...next };
  },

  async recordReviewsForSession(studentId, portionIds, quality, now = new Date()) {
    if (!Array.isArray(portionIds) || portionIds.length === 0) return [];
    const results = [];
    for (const portionId of portionIds) {
      // eslint-disable-next-line no-await-in-loop
      const r = await srsService.recordReview(studentId, portionId, quality, now);
      results.push(r);
    }
    return results;
  },

  /**
   * Phase 4 §F.3 — feed self-quiz results into FSRS.
   * Each graded question carries a `portionId` and `correct` flag.
   * We aggregate to one review per portion (the student's worst answer
   * on that portion wins — a single wrong question should not be hidden
   * by a correct one on the same portion) and map to quality:
   *   all correct on the portion  → 4 (Good)
   *   at least one wrong            → 2 (Hard)
   * Never throws — quizzes shouldn't be blocked by SRS hiccups.
   */
  async recordQuizSignals(studentId, details, now = new Date()) {
    if (!studentId || !Array.isArray(details) || details.length === 0) return [];
    const worstByPortion = new Map();
    for (const d of details) {
      if (d == null || typeof d.portionId !== 'number') continue;
      const prev = worstByPortion.get(d.portionId);
      const wrong = !d.correct;
      if (prev === undefined) {
        worstByPortion.set(d.portionId, wrong);
      } else if (wrong) {
        worstByPortion.set(d.portionId, true);
      }
    }
    const results = [];
    for (const [portionId, anyWrong] of worstByPortion.entries()) {
      const quality = anyWrong ? 2 : 4;
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await srsService.recordReview(studentId, portionId, quality, now);
        results.push(r);
      } catch (err) {
        console.warn('recordQuizSignals: recordReview failed', err?.message);
      }
    }
    return results;
  },

  async getDueCards(studentId, onDate = new Date(), { includeMastered = false } = {}) {
    const dateStr = toDateString(onDate);
    try {
      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'reviewQueue'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        where('dueDate', '<=', dateStr),
        orderBy('dueDate', 'asc')
      );
      const snap = await getDocs(q);
      const cards = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const filtered = includeMastered
        ? cards
        : cards.filter((c) => c.state !== CARD_STATES.MASTERED);
      // Sort by retrievability ascending — the portions the student is
      // about to forget come first, so a teacher opening the queue sees
      // the most urgent portions at the top.
      return filtered.sort((a, b) => cardPriority(a, onDate) - cardPriority(b, onDate));
    } catch (e) {
      console.warn('getDueCards failed:', e.message);
      return [];
    }
  },

  async getCardsByStudent(studentId, { maxResults = 500 } = {}) {
    try {
      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'reviewQueue'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        orderBy('dueDate', 'asc'),
        fsLimit(maxResults)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('getCardsByStudent failed:', e.message);
      return [];
    }
  },

  async snoozeCard(studentId, portionId, days = 1) {
    const current = await srsService.getCard(studentId, portionId);
    const baseDate = current?.dueDate || toDateString(new Date());
    const dueDate = addDaysToString(baseDate, days);
    await srsService.upsertCard(studentId, portionId, { dueDate });
    return { dueDate };
  },

  async advanceCard(studentId, portionId) {
    const dueDate = toDateString(new Date());
    await srsService.upsertCard(studentId, portionId, { dueDate });
    return { dueDate };
  },

  async retireCard(studentId, portionId) {
    await srsService.upsertCard(studentId, portionId, {
      state: CARD_STATES.MASTERED
    });
    return { state: CARD_STATES.MASTERED };
  },

  async getDueSummaryForStudents(studentIds, onDate = new Date()) {
    const results = {};
    await Promise.all(
      (studentIds || []).map(async (sid) => {
        const due = await srsService.getDueCards(sid, onDate);
        results[sid] = due.length;
      })
    );
    return results;
  },

  /**
   * Aggregate mastery stats for the 480-unit heat-map. Returns a map
   * keyed by portionId (1..480) with the per-portion mastery level
   * so the UI can colour each cell in one pass.
   */
  async getMasteryMapForStudent(studentId, now = new Date()) {
    const cards = await srsService.getCardsByStudent(studentId, { maxResults: 600 });
    const map = {};
    for (const card of cards) {
      const r = currentRetrievability(card, now);
      map[card.portionId] = {
        state: card.state,
        retrievability: r,
        dueDate: card.dueDate,
        interval: card.interval || 0,
        stability: card?.fsrs?.stability || 0
      };
    }
    return map;
  }
};

/**
 * Heat-map helper for the 30-day momentum widget. Returns
 * `{ YYYY-MM-DD: count }` aggregating how many reviews happened each day.
 */
export const cardsToHeatmap = (cards, { field = 'lastReviewedAt' } = {}) => {
  const out = {};
  if (!Array.isArray(cards)) return out;
  for (const c of cards) {
    const ts = c?.[field];
    if (!ts) continue;
    const date =
      typeof ts.toDate === 'function'
        ? ts.toDate()
        : ts instanceof Date
          ? ts
          : new Date(ts);
    if (Number.isNaN(date.getTime())) continue;
    const key = toDateString(date);
    out[key] = (out[key] || 0) + 1;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Back-compat shim for legacy callers
// ---------------------------------------------------------------------------
// The older SM-2 stub exposed `qualityToGrade` (1..5 → 0..5 grade). A few
// test files import it directly. Keep it exported so we don't break them.
export const qualityToGrade = (quality) => {
  if (typeof quality !== 'number' || Number.isNaN(quality)) return 0;
  if (quality >= 5) return 5;
  if (quality >= 4) return 4;
  if (quality >= 3) return 3;
  if (quality >= 2) return 2;
  if (quality >= 1) return 1;
  return 0;
};
